import type { ProductType } from "@/types";

/**
 * IGNITE-104: affiliate products reached through a tokenised URL.
 *
 * Coles solves this in its Java backend: the token lives in an affiliate_product
 * table and /api/v1/ui/catalog/affiliate/product/{token} resolves it. We have no
 * equivalent service, so the token travels with the product itself, in the same
 * customFields.pim.goCommerce block every other product attribute uses.
 *
 * The field names are the ones Ops added to Transact (IGNITE-104, 2026-08-31):
 * affiliateProduct (boolean) and affiliateToken (string). affiliateProduct was
 * already on the catalogue before the ticket and is true on the -OP / -FR / -WT
 * partner variants; affiliateToken is new and Ops fills it in per promotion.
 *
 * Everything below reads them defensively so a product missing the fields simply
 * is not an affiliate product.
 */

/**
 * Direct /product/{slug} access to an affiliate product.
 *
 * The whole point of the ticket is that affiliate URLs should not be guessable,
 * which only holds if the ordinary product page refuses to render them. Kept as
 * a named constant because it is still to be confirmed - flip to false to let
 * the old URLs keep working.
 */
export const BLOCK_AFFILIATE_ON_PRODUCT_PAGE = true;

type GoCommerce = Record<string, any> | undefined;

const goCommerce = (product: ProductType | undefined): GoCommerce =>
  (product as any)?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
    ?.goCommerce;

/** True when Ops has flagged the product as an affiliate product in Transact */
export const isAffiliateProduct = (product: ProductType | undefined): boolean =>
  goCommerce(product)?.affiliateProduct === true;

export const affiliateToken = (
  product: ProductType | undefined
): string | null => {
  const token = goCommerce(product)?.affiliateToken;
  return typeof token === "string" && token.trim() ? token.trim() : null;
};

/**
 * Whether the product's promotional discount is currently running.
 *
 * The discount actually applied to the price names its plan
 * (productOfferingPrice.recurring[].discount.discountPlanCode), so the matching
 * entry in allowedDiscountPlans is the one whose window decides validity. When
 * no discount is applied to the price, any plan whose window covers today counts.
 *
 * Only the date window is checked, not status: the catalogue carries plans in
 * DRAFT that are nonetheless applied to the price, so requiring ACTIVE would
 * hide products that are visibly discounted.
 */
export const hasValidDiscount = (
  product: ProductType | undefined,
  now: Date = new Date()
): boolean => {
  const plans = (product as any)?.allowedDiscountPlans;
  if (!Array.isArray(plans) || !plans.length) return false;

  const inWindow = (plan: any) => {
    if (!plan || plan.disabled === true) return false;
    const start = plan.startDate ? new Date(plan.startDate) : null;
    const end = plan.endDate ? new Date(plan.endDate) : null;
    if (start && Number.isFinite(start.getTime()) && now < start) return false;
    if (end && Number.isFinite(end.getTime()) && now > end) return false;
    return true;
  };

  const appliedCode = (product as any)?.productOfferingPrice?.recurring?.find(
    (price: any) => price?.discount?.discountPlanCode
  )?.discount?.discountPlanCode;

  if (appliedCode) {
    const applied = plans.find((plan: any) => plan?.code === appliedCode);
    // The applied plan is the authority when we can find it
    if (applied) return inWindow(applied);
  }

  return plans.some(inWindow);
};

/**
 * Whether /product/{slug} should refuse to render this product.
 *
 * A token is required on top of the flag, and that matters. affiliateProduct is
 * already true on 23 of the 30 live products (every -OP / -FR / -WT variant)
 * while only the ones Ops has set up for a promotion carry a token. Blocking on
 * the flag alone would take the untokenised ones off the product page with no
 * tokenised URL to reach them through - they would simply be unsellable.
 *
 * Requiring the token means a product disappears from the public page exactly
 * when it becomes reachable at /affiliate/{token}, never before. The trade-off:
 * a product Ops flags but forgets to tokenise stays publicly visible.
 */
export const isAffiliateOnlyProduct = (
  product: ProductType | undefined
): boolean => isAffiliateProduct(product) && affiliateToken(product) !== null;

/** Find the product a tokenised affiliate URL points at */
export const findProductByAffiliateToken = (
  products: ProductType[] | undefined,
  token: string
): ProductType | undefined => {
  if (!Array.isArray(products) || !token) return undefined;
  const wanted = token.trim().toLowerCase();
  return products.find(
    (product) =>
      isAffiliateProduct(product) &&
      affiliateToken(product)?.toLowerCase() === wanted
  );
};
